
import React, { useEffect } from 'react';
import NotFoundStep from './NotFoundStep';
import { GoCampaignProvider } from '@/context/GoCampaignContext';
import ManageGoCampaignStepRefactored from './ManageGoCampaignStepRefactored';
import { ManageGoPlanStep } from './go-plan';
import { Business } from '@/schemas/business'; // Changed to Zod-inferred type
import { useNavigate } from 'react-router-dom';

interface OnboardingStepProps {
  step: number;
  business: Business | undefined;
  businessId: string | undefined;
  onNavigateHome: () => void;
  onSetStep: (step: number) => void;
  onFinishOnboarding: () => void;
  onPackageSelected: (packageId: string) => void;
}

/**
 * Component that renders the appropriate step content based on current onboarding step
 */
const OnboardingStep: React.FC<OnboardingStepProps> = ({
  step,
  business,
  businessId,
  onNavigateHome,
  onSetStep,
  onFinishOnboarding,
  onPackageSelected
}) => {
  const navigate = useNavigate();

  // Enhanced logging for debugging component rendering
  useEffect(() => {
    console.log('OnboardingStep - RENDERING with:', { 
      step, 
      businessId, 
      businessName: business?.name, // Use name or displayName from Zod type
      businessDisplayName: business?.displayName,
      businessStatus: business?.status,
      onPackageSelectedFunction: !!onPackageSelected
    });
  }, [step, business, businessId, onPackageSelected]);

  // Check if business status is not onboarding and redirect if needed
  useEffect(() => {
    // Only redirect if business status is explicitly not 'onboarding'
    // AND we are not already on a gocampaign path (user might be intentionally editing)
    // This prevents premature redirects when status might be undefined during loading
    const currentPath = window.location.pathname;
    if (business && business.status && business.status !== 'onboarding' && !currentPath.includes('/gocampaign')) {
      console.log('OnboardingStep - Business is not in onboarding status and not on gocampaign path, redirecting to details page');
      navigate(`/business/${businessId}`, { replace: true });
    } else if (business && business.status && business.status !== 'onboarding' && currentPath.includes('/gocampaign')) {
      console.log('OnboardingStep - Business is not in onboarding status, but user is on gocampaign path. No redirect.');
    }
  }, [business, businessId, navigate]);

  if (!business) {
    console.log('OnboardingStep - Business not found, rendering NotFoundStep');
    return <NotFoundStep onNavigateHome={onNavigateHome} />;
  }

  // Use switch statement with explicit returns to ensure clean component rendering
  switch (step) {
    case 0:
      console.log('OnboardingStep - Rendering ManageGoPlanStep');
      return (
        <ManageGoPlanStep
          onNext={() => {
            console.log('ManageGoPlanStep.onNext called, setting step to 1');
            onSetStep(1);
          }} 
          onPackageSelected={(packageId) => {
            console.log('ManageGoPlanStep.onPackageSelected called with:', packageId);
            onPackageSelected(packageId);
          }}
        />
      );
    case 1:
      console.log('OnboardingStep - Rendering ManageGoCampaignStepRefactored');
      return (
        <GoCampaignProvider businessId={businessId || ''}>
          <ManageGoCampaignStepRefactored 
            businessId={businessId || ''} 
            onComplete={onFinishOnboarding}
          />
        </GoCampaignProvider>
      );
    default:
      console.log('OnboardingStep - Rendering NotFoundStep (default case)');
      return <NotFoundStep onNavigateHome={onNavigateHome} />;
  }
};

export default OnboardingStep;
